import React, { useEffect, useState } from "react";
import { ArrowLeft, BookOpen, CalendarRange, LockKeyhole, Quote, Radio, Sparkles, UserPlus } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchArchiveProfile, toggleArchiveFollow, type ArchiveProfile as ArchiveProfileType } from "../api/archive";
import FooterNav from "../components/FooterNav";
import { requireIdentity } from "../utils/identityGate";

export default function ArchiveProfile() {
    const { slug = "" } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<ArchiveProfileType | null>(null);
    const [loading, setLoading] = useState(true);
    const [followLoading, setFollowLoading] = useState(false);
    const [notice, setNotice] = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true);
        fetchArchiveProfile(slug)
            .then((data) => alive && setProfile(data))
            .catch(() => alive && setProfile(null))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [slug]);

    const handleFollow = async () => {
        if (!profile || !requireIdentity(`follow ${profile.name}`, `/archive/${profile.slug}`)) return;
        setFollowLoading(true);
        setNotice("");
        try {
            const next = await toggleArchiveFollow(profile.slug);
            setProfile((previous) => previous ? { ...previous, isFollowing: next.isFollowing, followerCount: next.followerCount } : previous);
        } catch (error: any) {
            setNotice(error?.message || "Could not update follow.");
        } finally {
            setFollowLoading(false);
        }
    };

    if (loading) {
        return <div className="bs-archive-page grid min-h-screen place-items-center"><div className="bs-archive-loader" aria-label="Loading archive profile" /></div>;
    }

    if (!profile) {
        return (
            <div className="bs-archive-page grid min-h-screen place-items-center px-6 text-white">
                <div className="bs-archive-empty max-w-md"><BookOpen size={28} /><strong>Signal not found</strong><span>This identity is not preserved in the Archive.</span><button type="button" onClick={() => navigate("/archive")}>Return to Archive</button></div>
            </div>
        );
    }

    const isLoggedIn = Boolean(localStorage.getItem("token"));

    return (
        <div className="bs-archive-page bs-page relative min-h-screen overflow-hidden text-white">
            <div className="bs-archive-haze" aria-hidden="true" />
            <main className="relative z-10 mx-auto w-full max-w-6xl px-4 pb-32 pt-4 sm:px-7 sm:pt-7">
                <header className="bs-archive-topbar">
                    <button type="button" onClick={() => navigate("/archive")} className="bs-archive-icon-button" aria-label="Back to Archive"><ArrowLeft size={17} /></button>
                    <button type="button" onClick={() => navigate("/home")} className="bs-archive-wordmark" aria-label="Blackspace home"><span>Black</span><strong>space</strong><i /></button>
                    <span className="bs-archive-live"><Radio size={13} /> preserved</span>
                </header>

                <section className="bs-profile-hero">
                    <aside className="bs-profile-identity">
                        <div className="bs-profile-portrait-frame">
                            <span className={`bs-profile-portrait bg-gradient-to-br ${profile.portraitTone}`}>{profile.initials}</span>
                            <small>{profile.era}</small>
                        </div>
                        <button type="button" onClick={handleFollow} disabled={followLoading} className={`bs-profile-follow ${profile.isFollowing ? "is-following" : ""}`}>
                            {isLoggedIn ? <UserPlus size={15} /> : <LockKeyhole size={15} />}
                            {followLoading ? "Updating" : profile.isFollowing ? "Following signal" : "Follow signal"}
                        </button>
                        {notice && <p className="bs-profile-notice">{notice}</p>}
                        <div className="bs-profile-stats"><span><strong>{profile.followerCount}</strong><small>followers</small></span><span><strong>{profile.themes.length}</strong><small>themes</small></span></div>
                    </aside>

                    <div className="bs-profile-copy">
                        <p className="bs-archive-kicker"><Sparkles size={13} /> Preserved identity / {profile.discipline}</p>
                        <h1>{profile.name}</h1>
                        <h2>{profile.title}</h2>
                        <p className="bs-profile-summary">{profile.summary}</p>
                        <div className="bs-profile-themes">{profile.themes.map((theme) => <span key={theme}>{theme}</span>)}</div>
                        {profile.quote && <blockquote className="bs-profile-quote"><Quote size={19} /><p>“{profile.quote}”</p>{profile.quoteContext && <cite>{profile.quoteContext}</cite>}</blockquote>}
                    </div>
                </section>

                <section className="bs-profile-ledger">
                    <article className="bs-profile-panel">
                        <header><CalendarRange size={16} /><span>Timeline</span><small>{String(profile.timeline.length).padStart(2, "0")}</small></header>
                        <div className="bs-profile-timeline">{profile.timeline.map((item) => <div key={`${item.year}-${item.text}`}><time>{item.year}</time><p>{item.text}</p></div>)}</div>
                    </article>
                    <article className="bs-profile-panel">
                        <header><BookOpen size={16} /><span>Works &amp; signals</span><small>{String(profile.works.length).padStart(2, "0")}</small></header>
                        <ol className="bs-profile-works">{profile.works.map((work, index) => <li key={work}><span>{String(index + 1).padStart(2, "0")}</span>{work}</li>)}</ol>
                    </article>
                </section>
            </main>
            <footer className="bs-archive-footer fixed inset-x-0 bottom-0 z-30"><FooterNav /></footer>
        </div>
    );
}
